import { motion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({
  children,
  delay = 0,
  y = 32,
  className = "",
  once = true,
}) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: "-70px" }}
    transition={{ duration: 0.75, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

// Masked line-by-line reveal. `lines` is an array of strings or JSX nodes.
export const MaskedLines = ({
  lines,
  className = "",
  lineClassName = "",
  delay = 0,
  stagger = 0.14,
  trigger = "view",
}) => {
  const anim =
    trigger === "mount"
      ? { animate: "show" }
      : { whileInView: "show", viewport: { once: true, margin: "-60px" } };
  return (
    <motion.span
      className={className}
      initial="hidden"
      {...anim}
      variants={{ hidden: {}, show: { transition: { staggerChildren: stagger, delayChildren: delay } } }}
    >
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
          <motion.span
            className={`block will-change-transform ${lineClassName}`}
            variants={{
              hidden: { y: "115%" },
              show: { y: "0%", transition: { duration: 0.85, ease: EASE } },
            }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </motion.span>
  );
};

// Numbered manifesto chapter heading: "01 — The Problem" + big masked title.
export const ChapterHeading = ({
  no,
  kicker,
  titleLines,
  sub,
  dark = false,
  className = "",
}) => (
  <div className={className}>
    <Reveal>
      <div
        className={`flex items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.35em] ${
          dark ? "text-white/50" : "text-[#07130C]/50"
        }`}
      >
        <span className="text-gradient-pulse text-sm tracking-normal font-bold">{no}</span>
        <span className={`h-px w-12 ${dark ? "bg-white/25" : "bg-[#07130C]/20"}`} />
        <span>{kicker}</span>
      </div>
    </Reveal>
    <MaskedLines
      lines={titleLines}
      delay={0.08}
      className={`mt-6 block font-display text-4xl font-semibold leading-[1.04] tracking-tight md:text-5xl lg:text-6xl ${
        dark ? "text-white" : "text-[#07130C]"
      }`}
    />
    {sub && (
      <Reveal delay={0.35}>
        <p
          className={`mt-6 max-w-2xl text-base leading-relaxed md:text-lg ${
            dark ? "text-white/60" : "text-[#07130C]/60"
          }`}
        >
          {sub}
        </p>
      </Reveal>
    )}
  </div>
);

// Simple page fade used for route transitions.
export const PageFade = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.35, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);
